import { assign, createMachine, interpret } from 'xstate'

import { EventDescription, trackInteraction } from '../lib/mockDigitalData'

export enum Interactions {
  contributionSlider = 'contribution slider',
  annualConsumption = 'annual consumption dropdown',
  paymentFrequency = 'payment frequency dropdown',
  currentCost = 'current cost input',
  contactButton = 'contact button',
  emailAddress = 'email address input',
  contactForm = 'contact form',
  submitButton = 'submit button',
}

export type EventStage = 'start' | 'interacted' | 'abandon' | 'complete'

const trackingMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QBUBOBDAxgawJYDsoBiAVQAcJ0AXMAAgjk1VzKtwHt8BtABgF1EoMu1i42nQSAAeiAOyyAdAE4ArAGYAjACYVAGhABPRAFolAFgBsCrRvVqtPTToC+z-Wix5CCgJIQANmBEPvg0GJhUvAJIIMKi4viSMgjGZmYayuraeoYmZgAcVipmSpoWZjwWLm4gHjgEUL6hYOE0EMHNrVGScWIciTHJxhpmWsr5sqUa+bb5c1r6Rika9goaFhb5KvmWZmo8e67u4V6NIWFYbR0XEVwa0UIifRKDeUqK5iOVOosm5gpVJTzGbVY6eBpNG5XAAyYHQADcwN0Yr0Ekk8opZA4VLIzLJbJU1EoLL8UioePkFAU1GpyUSNjNDkcQPh2Ax4DE6qcek80a8yfkxkobBMcktjLIVCoFLILKonCpmVyIX5Ajz4v10SkShlyjs1JLScYLAdrHKsqDaicVZ1LpB1c8BqAhuozMoRYbcik1BUZVpJUTvoqasrvABhdgAWzIgRoDr5zsQ-spZhU6y0QOFOzFJh2ygqGZUxL2BxpSut3gAggAjdD4CAvR4axvSJP5Hgyk02dKySo8d5mI0+6VaTZKEbyceWLSuVxAA */
  createMachine(
    {
      context: { interactions: [] },
      tsTypes: {} as import('./tracking.machine.typegen').Typegen0,

      schema: {
        context: {} as { interactions: Interactions[]; description?: EventDescription },
        events: {} as
          | { type: 'Interact'; name: Interactions }
          | { type: 'Leave' }
          | { type: 'Update description'; description: EventDescription },
      },

      preserveActionOrder: true,
      predictableActionArguments: true,

      on: {
        'Update description': {
          actions: 'Save description',
          description: 'Saves the *description* data to be sent with the "complete" tracking event.',
        },
      },

      description:
        'For the lifetime of the **Tracking** service, watch the contact form for interaction or submission.',

      id: 'Tracking',
      initial: 'Idle',

      states: {
        Idle: {
          description: 'The initial state before any interaction.',
          on: {
            Interact: {
              target: 'Interacted',
              actions: ['Track start', 'Track interacted'],
            },
          },
        },
        Interacted: {
          description:
            'Now that the user has started interacting, we need to watch for users leaving *(Watch for unload)* before completing their journey.',
          invoke: [
            {
              src: 'Watch for unload',
            },
            {
              src: 'Watch for form interaction',
            },
            {
              src: 'Watch for form submit',
            },
          ],
          on: {
            Interact: [
              {
                target: 'Complete',
                cond: 'Form submitted',
                actions: 'Track complete',
                description: 'Called if the *Watch for form submit* service has sent an **Interact** event.',
                internal: true,
              },
              {
                target: 'Interacted',
                actions: 'Track interacted',
                description: '...otherwise it is a normal interaction.',
                internal: true,
              },
            ],
            Leave: {
              target: 'Abandon',
              actions: 'Track abandon',
              description: 'Called from the  *Watch for unload* service.',
            },
          },
        },
        Complete: {
          type: 'final',
        },
        Abandon: {
          type: 'final',
        },
      },
    },
    {
      guards: {
        'Form submitted': (_ctx, evt) => evt.type === 'Interact' && evt.name === Interactions.submitButton,
      },
      services: {
        'Watch for unload': () => (send) => {
          const handleBeforeUnload = (e: BeforeUnloadEvent): string => {
            send({ type: 'Leave' })
            return (e.returnValue = '')
          }
          window.addEventListener('beforeunload', handleBeforeUnload)
          return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
          }
        },
        'Watch for form submit': () => (send) => {
          const form = document.querySelector('form[name="buyOnline"]')
          const handleFormSubmit = () => {
            send({ type: 'Interact', name: Interactions.submitButton })
          }
          form?.addEventListener('submit', handleFormSubmit)
          return () => {
            form?.removeEventListener('submit', handleFormSubmit)
          }
        },
        'Watch for form interaction': () => (send) => {
          const form = document.querySelector('form[name="buyOnline"]')
          const handleFormChange = (): void => {
            send({ type: 'Interact', name: Interactions.contactForm })
          }
          form?.addEventListener('input', handleFormChange, { once: true })
          return () => {
            form?.removeEventListener('input', handleFormChange)
          }
        },
      },
      actions: {
        'Track start': assign(({ interactions }, { name }) => {
          trackInteraction('start', name)
          return { interactions: [...interactions, name] }
        }),
        'Track interacted': assign(({ interactions }, { name }) => {
          trackInteraction('interacted', name)
          return { interactions: [...interactions, name] }
        }),
        'Track complete': assign(({ interactions, description }, { name }) => {
          trackInteraction('complete', name, description)
          return { interactions: [...interactions, name] }
        }),
        'Track abandon': ({ interactions }) => {
          trackInteraction('abandon', interactions[interactions.length - 1])
        },
        'Save description': assign((_ctx, { description }) => ({ description })),
      },
    },
  )

export const trackingService = interpret(trackingMachine).start()
